// Отрыв реальных GLB-кубов: сабмеш уходит с тела и летит как жёсткое тело
// (гравитация / отскок как у debris), каждый кадр — тот же VAO, своя model matrix.

import { mat4Multiply } from "./math";
import { GRAVITY } from "./voxel";
import type { MeshInstance } from "./renderer";
import type { LoadedMesh, SubMesh } from "./meshLoader";

function isVoxelCarcassSubmesh(inner: LoadedMesh, sm: SubMesh): boolean {
  if (sm.bodyLayer === "inner") return true;
  const low = (inner.nodes[sm.nodeIdx]?.name ?? "").toLowerCase();
  return low.includes("voxel") || low.includes("carcass") || low.includes("blocks");
}

export interface RealVoxelFlyPiece {
  subIdx: number;
  /** im·nodeWorld в момент отрыва (column-major); для скиннутых сабмешей не используется при отрисовке. */
  baseModel: Float32Array;
  /** Скиннутый куб: снимок uBones + uModel(im) на кадр отрыва — иначе куски «ездят» с телом или схлопываются. */
  frozenBones: Float32Array | null;
  spawnIm: Float32Array | null;
  /** Мировой центр куба в момент отрыва. */
  c0x: number;
  c0y: number;
  c0z: number;
  px: number;
  py: number;
  pz: number;
  vx: number;
  vy: number;
  vz: number;
  born: number;
}

const MAX_FLY_PER_INST = 220;
const FLY_LIFETIME_SEC = 4.2;
const TORSO_Z_FRAC = 0.48;

function buildActorIm(inst: MeshInstance, out: Float32Array): void {
  const finalYaw = inst.yaw + inst.yawOffset;
  const c = Math.cos(finalYaw);
  const s = Math.sin(finalYaw);
  const sc = inst.scale;
  out[0] = sc * c;
  out[1] = sc * s;
  out[2] = 0;
  out[3] = 0;
  out[4] = 0;
  out[5] = 0;
  out[6] = sc;
  out[7] = 0;
  out[8] = sc * s;
  out[9] = -sc * c;
  out[10] = 0;
  out[11] = 0;
  out[12] = inst.x;
  out[13] = inst.y;
  out[14] = inst.z;
  out[15] = 1;
}

/** Мировые координаты центроида сабмеша и полная model matrix im·nodeWorld. */
export function computeInnerSubmeshWorldModel(
  inst: MeshInstance,
  si: number,
  centroids: Float32Array,
  outModel: Float32Array,
  outCenter: [number, number, number],
): boolean {
  const inner = inst.innerMesh;
  if (!inner || si < 0 || si >= inner.subMeshes.length) return false;
  const sm = inner.subMeshes[si]!;
  if (!isVoxelCarcassSubmesh(inner, sm)) return false;
  const inw = inst.innerNodeWorldMatrices;
  if (inw.length < inner.nodes.length * 16) return false;

  const im = new Float32Array(16);
  buildActorIm(inst, im);
  const nwOff = sm.nodeIdx * 16;
  mat4Multiply(outModel, im, inw.subarray(nwOff, nwOff + 16));

  const cx = centroids[si * 3] ?? 0;
  const cy = centroids[si * 3 + 1] ?? 0;
  const cz = centroids[si * 3 + 2] ?? 0;
  outCenter[0] = outModel[0] * cx + outModel[4] * cy + outModel[8] * cz + outModel[12];
  outCenter[1] = outModel[1] * cx + outModel[5] * cy + outModel[9] * cz + outModel[13];
  outCenter[2] = outModel[2] * cx + outModel[6] * cy + outModel[10] * cz + outModel[14];
  return true;
}

function spawnVelocity(inst: MeshInstance, wx: number, wy: number, wz: number): [number, number, number] {
  const pivotZ = inst.z + TORSO_Z_FRAC * 1.8;
  let dx = wx - inst.x;
  let dy = wy - inst.y;
  let dz = wz - pivotZ;
  const dist = Math.hypot(dx, dy, dz) || 1;
  dx /= dist;
  dy /= dist;
  dz /= dist;
  const speed = 3.0 + Math.random() * 3.8;
  const j = 0.55;
  const vx = dx * speed + (Math.random() - 0.5) * j;
  const vy = dy * speed + (Math.random() - 0.5) * j;
  const vz = dz * speed * 0.95 + 1.3 + Math.random() * 2.0;
  return [vx, vy, vz];
}

export function trySpawnRealVoxelFlyPiece(
  inst: MeshInstance,
  si: number,
  centroids: Float32Array,
  timeAcc: number,
): void {
  if (!inst.realVoxelBody || !inst.innerMesh) return;
  if (inst.realVoxelFlying.length >= MAX_FLY_PER_INST) return;

  const base = new Float32Array(16);
  const c: [number, number, number] = [0, 0, 0];
  if (!computeInnerSubmeshWorldModel(inst, si, centroids, base, c)) return;

  const sm = inst.innerMesh.subMeshes[si]!;
  let frozenBones: Float32Array | null = null;
  let spawnIm: Float32Array | null = null;
  if (sm.hasSkin && inst.innerBoneMatrices.length > 0) {
    frozenBones = new Float32Array(inst.innerBoneMatrices);
    spawnIm = new Float32Array(16);
    buildActorIm(inst, spawnIm);
  }

  const [vx, vy, vz] = spawnVelocity(inst, c[0], c[1], c[2]);
  inst.realVoxelFlying.push({
    subIdx: si,
    baseModel: base,
    frozenBones,
    spawnIm,
    c0x: c[0],
    c0y: c[1],
    c0z: c[2],
    px: c[0],
    py: c[1],
    pz: c[2],
    vx,
    vy,
    vz,
    born: timeAcc,
  });
}

export function updateRealVoxelFlyPhysics(inst: MeshInstance, dt: number, timeAcc: number): void {
  const fly = inst.realVoxelFlying;
  if (fly.length === 0) return;
  let w = 0;
  for (let i = 0; i < fly.length; i++) {
    const p = fly[i]!;
    if (timeAcc - p.born > FLY_LIFETIME_SEC || p.pz < -0.5) continue;

    p.vz -= GRAVITY * dt;
    p.px += p.vx * dt;
    p.py += p.vy * dt;
    p.pz += p.vz * dt;

    if (p.pz < 0) {
      p.pz = 0;
      const sp = Math.abs(p.vz);
      if (sp < 0.45) {
        p.vz = 0;
        p.vx *= 0.55;
        p.vy *= 0.55;
      } else {
        p.vz = -p.vz * 0.22;
        const energy = Math.min(1, sp / 5.0);
        p.vx = p.vx * 0.35 + (Math.random() - 0.5) * 3.5 * energy;
        p.vy = p.vy * 0.35 + (Math.random() - 0.5) * 3.5 * energy;
      }
    }
    fly[w++] = p;
  }
  fly.length = w;
}
